import {
  useV2CreateVendorMutation,
  useV2UpdateVendorMutation,
  useV2DeleteVendorMutation,
} from "@generated";
import type { V2VendorResource } from "@generated";
import type { VendorFormValues } from "../types";

// Build the JSON:API `data` body for create/update. The `account` relationship
// required by V2VendorResource is inferred from auth server-side, so we omit it
// and cast (same pattern as the Clients MSPA).
export const buildVendorData = (
  values: VendorFormValues,
  id?: string
): V2VendorResource => {
  const country =
    typeof values.country === "string"
      ? values.country
      : values.country?.id ?? "";

  return {
    type: "vendors",
    ...(id ? { id: Number(id) } : {}),
    attributes: {
      name: values.name,
      "street-address": values.streetAddress || "",
      street2: values.street2 || "",
      city: values.city || "",
      state: values.state || "",
      "postal-code": values.postalCode || "",
      country,
    },
  } as unknown as V2VendorResource;
};

export const useVendorMutations = () => {
  const [create, createState] = useV2CreateVendorMutation();
  const [update, updateState] = useV2UpdateVendorMutation();
  const [destroy, destroyState] = useV2DeleteVendorMutation();

  return {
    create: (values: VendorFormValues) =>
      create({ body: { data: buildVendorData(values) } }).unwrap(),

    update: (id: string, values: VendorFormValues) =>
      update({
        id: Number(id),
        body: { data: buildVendorData(values, id) },
      }).unwrap(),

    // Archive (soft-delete: the API stamps deleted_at; restore re-activates).
    archive: (id: string) => destroy({ id: Number(id) }).unwrap(),

    restore: (id: string, name: string) =>
      update({
        id: Number(id),
        body: {
          data: {
            type: "vendors",
            id: Number(id),
            // `active` is not in the generated V2VendorResource schema for vendors (it
            // is for clients); the cast lets restore re-activate a soft-deleted vendor.
            // The vendor soft-delete/active behavior is verified manually (plan Task 13).
            attributes: { name, active: true },
          } as unknown as V2VendorResource,
        },
      }).unwrap(),

    states: { createState, updateState, destroyState },
  };
};

export default useVendorMutations;
